<template>
  <div v-if="initialLoadComplete">
    <p>
      Answers can be deleted if they have no security functions associated with
      them AND they have not be saved to an Organization Role's Question and
      Answer snapshot in the Build section.
    </p>
    <v-data-table
      :headers="headers"
      :items="filteredList"
      hide-default-footer
      no-data-text="No answers for this question"
      class="och-table"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>
            <a
              href="#"
              @click="
                $router.push({
                  name: 'ochinUserSecurityAnswerForm',
                  params: { answerId: item.id },
                })
              "
              >{{ item.text }}</a
            >
          </td>
          <td>
            <div>
              ({{ filterAnswerFunction("secAnswerId", item.id).length }}) -
              {{
                filterAnswerFunction("secAnswerId", item.id)
                  .map(func => {
                    return getFunctionById(func.secFunctionId).name;
                  })
                  .sort()
                  .join(", ")
              }}
            </div>
          </td>
          <td>
            <div>
              <ul>
                <li
                  v-for="question in filterQuestionList(
                    'parentAnswerId',
                    item.id
                  )"
                  :key="question.id"
                >
                  {{ question.text }}
                </li>
              </ul>
            </div>
          </td>
          <td>
            <v-checkbox
              :disabled="questionIsDeprecated"
              @click.stop="setDeprecationStatus(item)"
              v-model="item.isDeprecated"
            ></v-checkbox>
          </td>
          <td class="text-right">
            <v-btn
              :disabled="!!item.isDeprecated"
              @click.stop="
                $router.push({
                  name: 'ochinUserSecurityQuestionCreate',
                  query: { parentAnswerId: item.id },
                })
              "
              class="mr-3"
              color="primary"
              outlined
              small
              >Add Child Question</v-btn
            >
            <v-btn
              :disabled="!!isAnswerInUse(item.id)"
              color="primary"
              small
              outlined
              @click.stop="deleteAnswer(item.id)"
              >Delete</v-btn
            >
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  computed: {
    questionId() {
      return parseInt(this.$route.params.questionId, 10);
    },
    filteredList() {
      return this.filterAnswerList("secQuestionId", this.questionId);
    },
    ...mapGetters({
      filterAnswerList: "secAnswer/filterByAttributeValue",
      filterAnswerFunction: "secAnswerSecFunction/filterByAttributeValue",
      filterRoleOrganizationAnswer:
        "secRoleOrganizationSecAnswer/filterByAttributeValue",
      filterQuestionList: "secQuestion/filterByAttributeValue",
      getFunctionById: "secFunction/getById",
      getQuestionById: "secQuestion/getById",
    }),
    ...mapState({
      list: state => state.secAnswer.list,
    }),
  },
  async created() {
    await Promise.all([
      this.getAnswerList(),
      this.getAnswerFunctionList(),
      this.getFunctionList(),
      this.getQuestionList(),
      this.getRoleOrganizationAnswerList(),
    ]);
    this.initialLoadComplete = true;
  },
  data() {
    return {
      headers: [
        {
          text: "Answer",
          value: "text",
          align: "start",
        },
        {
          text: "Security Functions",
          value: "functions",
          align: "start",
          sortable: false,
        },
        {
          text: "Child Questions",
          value: "questions",
          align: "start",
          sortable: false,
        },
        {
          text: "Deprecated",
          value: "isDeprecated",
          align: "start",
          sortable: false,
        },
        {
          text: "",
          value: "",
          align: "start",
          sortable: false,
        },
      ],
      initialLoadComplete: false,
    };
  },
  methods: {
    isAnswerInUse(id) {
      const roleOrganizationAnswerList = this.filterRoleOrganizationAnswer(
        "secAnswerId",
        id
      );
      const answerFunctionList = this.filterAnswerFunction("secAnswerId", id);
      const parentAnswerIdList = this.filterQuestionList("parentAnswerId", id);

      if (
        roleOrganizationAnswerList.length === 0 &&
        answerFunctionList.length == 0 &&
        parentAnswerIdList.length === 0
      ) {
        return false;
      }
      return true;
    },
    async deleteAnswer(id) {
      try {
        await this.removeAnswer(id);
      } catch (e) {
        console.error(e);
        if (e.response.status === 409) {
          this.$notifyWarn({
            message: "Unable to delete answer. Answer in use.",
            timeout: false,
          });
        }
        this.$notifyError({ message: "Could not delete answer." });
      }
    },
    async setDeprecationStatus(item) {
      // Find all child questions and deprecate recursively.
      const childQuestionList = this.filterQuestionList(
        "parentAnswerId",
        item.id
      );
      for (let question of childQuestionList) {
        try {
          if (item.isDeprecated) {
            await this.updateDeprecationStatus(question.id, false);
          } else {
            await this.updateDeprecationStatus(question.id, true);
          }
        } catch (e) {
          console.error(e);
          this.$notifyError({ message: "Unable to update child questions." });
        }
      }
      // Set the `isDeprecated` value to the opposite of its current state.
      const isDeprecated = item.isDeprecated ? 0 : 1;
      try {
        await this.updateAnswer([item.id, { isDeprecated }]);
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Unable to update answer." });
      }
      await this.getAnswerList();
    },
    async updateDeprecationStatus(id, deprecate = false) {
      // Iterate over children and deprecate all child questions and answers recursively.
      const answerList = this.filterAnswerList("secQuestionId", id);
      const isDeprecated = deprecate ? 1 : 0;

      let result = [];
      for (let answer of answerList) {
        const childQuestionList = this.filterQuestionList(
          "parentAnswerId",
          answer.id
        );
        for (let question of childQuestionList) {
          // Recursively deprecating child questions.
          const returnVal = await this.updateDeprecationStatus(
            question.id,
            deprecate
          );
          result = result.concat(returnVal);
        }

        this.updateAnswer([answer.id, { isDeprecated }]);
      }

      await this.updateQuestion([id, { isDeprecated }]);
      result.push(id);

      // The returned result is an array of question and child question ids.
      return result;
    },
    ...mapActions({
      getAnswer: "secAnswer/get",
      getAnswerList: "secAnswer/list",
      getAnswerFunctionList: "secAnswerSecFunction/list",
      getFunctionList: "secFunction/list",
      getQuestionList: "secQuestion/list",
      getRoleOrganizationAnswerList: "secRoleOrganizationSecAnswer/list",
      removeAnswer: "secAnswer/remove",
      updateAnswer: "secAnswer/update",
      updateQuestion: "secQuestion/update",
    }),
  },
  props: {
    questionIsDeprecated: {
      default: false,
      type: Boolean,
    },
  },
};
</script>
